// ============================================================================
// Rule-based "agent" matcher — maps a free-text question to a guide topic.
// ============================================================================
//
// This is the "Agent layer" from the feature spec, kept deliberately simple:
// NO LLM, NO network, no new deps. It scores the user's question against each
// topic's `keywords` (English + Hinglish) and returns the best match. Shares
// the single source of truth in guideContent.ts, so adding a keyword there
// improves both the matcher and the UI.
//
// ⚠️ KEEP IN SYNC with the agent app copy:
//    pro_da_agent/cmd/prodaagent/frontend/src/lib/guide/matchGuideTopic.ts
// ============================================================================

import { GUIDE_TOPICS, type GuideTopic } from './guideContent';

export interface GuideMatch {
  topic: GuideTopic;
  /** Number of keyword hits — lets the UI show confidence / sort if needed. */
  score: number;
}

/**
 * Returns the best-matching topic for `query`, or null if nothing matches.
 * Matching is case-insensitive substring containment against each topic's
 * keyword list. Longer keywords are weighted slightly higher so a specific
 * phrase ("smart codec") beats a generic token ("fps") on ties.
 */
export function matchGuideTopic(query: string): GuideMatch | null {
  const q = query.toLowerCase().trim();
  if (!q) return null;

  let best: GuideTopic | null = null;
  let bestScore = 0;

  for (const topic of GUIDE_TOPICS) {
    let score = 0;
    for (const kw of topic.keywords) {
      const k = kw.toLowerCase();
      if (q.includes(k)) {
        // Multi-word keywords are more specific → weight them higher.
        score += k.includes(' ') ? 2 : 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      best = topic;
    }
  }

  return best && bestScore > 0 ? { topic: best, score: bestScore } : null;
}
